import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, RefreshCw, RouteOff, Save } from "lucide-react";
import { getRouteRemovalPolicy, updateRouteRemovalPolicy } from "../../api/settings";
import { showToast } from "../../utils/showToast";
import { apiErrorCode, apiErrorMessage, apiErrorStatus } from "../../utils/apiErrorMessage";
import { SectionCard } from "../ui/SectionCard";
import { Badge } from "../ui/Badge";
import { cn } from "../../utils/cn";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Skeleton } from "../ui/Skeleton";

// ─────────────────────────────────────────────────────────────────────────────
// RouteRemovalPolicyConfig — Si los repartidores del operador pueden quitar
// pedidos de su ruta desde la app: siempre, nunca o solo cerca de una de sus
// instalaciones.
//
// NO es una sección de DeliveryConfig: la política vive en microservice-packages
// (GET/PATCH /packages/route-removal-policy, proxyado por el BFF), que es quien la
// aplica al quitar el pedido. El operador sale del JWT y solo su administrador
// puede leerla desde acá; cualquier otro usuario recibe 403.
//
// Sin política guardada el backend responde `always` con `isDefault: true`: a
// diferencia de la ruta obligatoria, acá el default sí se conoce y se preselecciona.
// ─────────────────────────────────────────────────────────────────────────────

const MODES = ["always", "never", "near_warehouse"];
const NEAR_WAREHOUSE = "near_warehouse";

// Mismos límites que el DTO del backend.
const RADIUS_MIN_METERS = 100;
const RADIUS_MAX_METERS = 10000;
// El backend no tiene radio por defecto: este es el que se sugiere si nunca se guardó.
const DEFAULT_RADIUS_METERS = 500;

const LOCATIONS_TAB_PATH = "/admin/settings?tab=locations";

const OPTIONS = [
  {
    value: "always",
    id: "route-removal-always",
    title: "Siempre",
    description: "El repartidor puede quitar pedidos de su ruta en cualquier momento.",
  },
  {
    value: "never",
    id: "route-removal-never",
    title: "Nunca",
    description: "El repartidor no puede quitar pedidos de su ruta.",
  },
  {
    value: NEAR_WAREHOUSE,
    id: "route-removal-near-warehouse",
    title: "Solo cerca de una instalación",
    description:
      "El repartidor solo puede quitar un pedido si su GPS lo ubica dentro del radio de alguna de tus instalaciones con ubicación en el mapa.",
  },
];

/**
 * Normaliza la respuesta del GET/PATCH. `null` si no trae un modo conocido: sin
 * modo no se puede mostrar la política sin inventarla.
 */
function policyFromResponse(raw) {
  if (!raw || !MODES.includes(raw.mode)) return null;
  return {
    mode: raw.mode,
    radiusMeters: Number.isInteger(raw.radiusMeters) ? raw.radiusMeters : null,
    isDefault: raw.isDefault === true,
    // Sin el dato se asume 0: deshabilitar la opción es más seguro que ofrecer
    // una que el backend rechazaría.
    geocodedCount: Number.isFinite(raw.geocodedCount) ? raw.geocodedCount : 0,
    warehouses: Array.isArray(raw.warehouses) ? raw.warehouses : [],
  };
}

/** Radio válido (entero entre los límites) o `null`. Acepta solo dígitos: "1e3" o "500.5" no. */
function parseRadius(input) {
  const text = String(input ?? "").trim();
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return value >= RADIUS_MIN_METERS && value <= RADIUS_MAX_METERS ? value : null;
}

function radiusInputFrom(policy) {
  return String(policy.radiusMeters ?? DEFAULT_RADIUS_METERS);
}

/**
 * Texto a mostrar cuando falla el PATCH. microservice-packages responde 422 con un
 * mensaje propio (p. ej. NO_GEOCODED_WAREHOUSES si se borraron las coordenadas
 * mientras la pantalla estaba abierta), 403 si el usuario no es admin del
 * operador y 400 con los mensajes de class-validator. Los 5xx y los fallos de
 * red no exponen texto interno.
 */
function saveErrorMessage(err, t) {
  const fallback = t("No se pudo guardar la configuración");
  const status = apiErrorStatus(err);
  if (status === 403) {
    return apiErrorMessage(err, t("No tienes permiso para modificar esta configuración."));
  }
  if (status === null || status >= 500) return fallback;
  return apiErrorMessage(err, fallback);
}

export default function RouteRemovalPolicyConfig() {
  const { t } = useTranslation();

  const [policy, setPolicy] = useState(null);
  const [mode, setMode] = useState("always");
  const [radiusInput, setRadiusInput] = useState(String(DEFAULT_RADIUS_METERS));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const applyPolicy = (next) => {
    setPolicy(next);
    setMode(next.mode);
    setRadiusInput(radiusInputFrom(next));
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getRouteRemovalPolicy()
      .then((res) => {
        if (!active) return;
        const next = policyFromResponse(res?.data ?? res);
        if (!next) throw new Error("Respuesta de la política sin modo válido");
        applyPolicy(next);
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
        } else {
          // No se muestran opciones con un valor inventado: Guardar pisaría la
          // política real del operador.
          setLoadError(true);
          showToast(t("No se pudo cargar la política para quitar pedidos de la ruta"), false);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // `t` fuera de las deps a propósito: su identidad cambia con el idioma y eso
    // refetchearía la política, descartando ediciones sin aviso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const isNearWarehouse = mode === NEAR_WAREHOUSE;
  const noGeocoded = (policy?.geocodedCount ?? 0) === 0;
  const radius = parseRadius(radiusInput);
  const radiusInvalid = isNearWarehouse && radius === null;

  // El radio solo cuenta como cambio en `near_warehouse`: en los otros modos no
  // se muestra ni viaja.
  const dirty = useMemo(() => {
    if (!policy) return false;
    if (mode !== policy.mode) return true;
    return isNearWarehouse && radiusInput.trim() !== radiusInputFrom(policy);
  }, [policy, mode, isNearWarehouse, radiusInput]);

  // `near_warehouse` sin instalaciones con coordenadas lo rechaza el backend (422).
  const canSave = dirty && !saving && !radiusInvalid && !(isNearWarehouse && noGeocoded);

  const handleSave = async () => {
    if (!canSave) return;
    // Fuera de `near_warehouse` el radio no se manda: el backend conserva el
    // guardado, así que volver a ese modo no obliga a escribirlo de nuevo.
    const payload = isNearWarehouse ? { mode, radiusMeters: radius } : { mode };
    setSaving(true);
    try {
      const res = await updateRouteRemovalPolicy(payload);
      // El PATCH responde la política completa; si no la trae, se asume lo enviado.
      const next = policyFromResponse(res?.data ?? res) ?? {
        ...policy,
        mode,
        radiusMeters: isNearWarehouse ? radius : policy.radiusMeters,
        isDefault: false,
      };
      applyPolicy(next);
      showToast(t("Política para quitar pedidos de la ruta guardada"), true);
    } catch (err) {
      showToast(saveErrorMessage(err, t), false);
      // Las coordenadas de las instalaciones cambiaron con la pantalla abierta: se
      // recarga para que la opción refleje el conteo real y quede deshabilitada.
      if (apiErrorCode(err) === "NO_GEOCODED_WAREHOUSES") setReloadKey((k) => k + 1);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (policy) applyPolicy(policy);
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("Sin configuración disponible")}
        subtitle={t("Solo los administradores de un operador logístico pueden configurar esta opción.")}
      />
    );
  }

  if (loadError || !policy) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("No se pudo cargar la configuración")}
        subtitle={t("Reintenta en un momento.")}
        action={
          <Button
            variant="outline"
            onClick={() => setReloadKey((k) => k + 1)}
            iconLeft={<RefreshCw size={16} />}
          >
            {t("Reintentar")}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon={<RouteOff className="w-4 h-4" />}
        title={t("Quitar pedidos de la ruta")}
        subtitle={t("Si tus repartidores pueden quitar pedidos de su ruta desde la app.")}
      >
        <div className="flex flex-col gap-4">
          {policy.isDefault && (
            <p
              className="flex items-start gap-1.5 text-xs text-fg-muted"
              data-testid="route-removal-default-note"
            >
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
              {t(
                "Usando la configuración por defecto de Beartrack: el repartidor puede quitar pedidos de su ruta siempre.",
              )}
            </p>
          )}

          <fieldset className="min-w-0" disabled={saving}>
            <legend className="text-sm font-medium text-fg mb-2">
              {t("Cuándo puede el repartidor quitar un pedido de su ruta")}
            </legend>
            <div
              role="radiogroup"
              aria-label={t("Cuándo puede el repartidor quitar un pedido de su ruta")}
              className="flex flex-col gap-2"
            >
              {OPTIONS.map((opt) => {
                const selected = mode === opt.value;
                const disabled = opt.value === NEAR_WAREHOUSE && noGeocoded;
                // La nota de por qué está deshabilitada va sin atenuar: es lo que hay que leer.
                const dimmed = disabled && "opacity-60";
                return (
                  <label
                    key={opt.id}
                    htmlFor={opt.id}
                    className={cn(
                      "flex items-start gap-3 rounded-lg border p-3 transition-colors",
                      disabled ? "cursor-not-allowed" : "cursor-pointer",
                      selected
                        ? "border-brand-500 bg-brand-50"
                        : "border-border bg-bg",
                      !selected && !disabled && "hover:border-fg-subtle",
                    )}
                  >
                    <input
                      type="radio"
                      id={opt.id}
                      name="route-removal-mode"
                      className={cn("mt-1", dimmed)}
                      checked={selected}
                      disabled={disabled}
                      onChange={() => setMode(opt.value)}
                    />
                    <span className="flex flex-col">
                      <span className={cn("text-sm font-medium text-fg", dimmed)}>
                        {t(opt.title)}
                      </span>
                      <span className={cn("text-xs text-fg-subtle", dimmed)}>
                        {t(opt.description)}
                      </span>
                      {disabled && (
                        <span
                          className="text-xs text-status-warning mt-1"
                          data-testid="route-removal-near-disabled-note"
                        >
                          {/* Guardada así (las coordenadas se borraron después) es el
                              único aviso de la tarjeta: dice qué pasa hoy con la flota. */}
                          {selected
                            ? t(
                                "Ninguna de tus instalaciones tiene ubicación en el mapa: con esta opción tus repartidores no pueden quitar pedidos de la ruta. Márcala en Ubicaciones o elige otra opción.",
                              )
                            : t(
                                "Ninguna de tus instalaciones tiene ubicación en el mapa. Para usar esta opción, márcala en Ubicaciones.",
                              )}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>

            {isNearWarehouse && (
              <div className="mt-4">
                <label
                  htmlFor="route-removal-radius"
                  className="block text-sm font-medium text-fg mb-1.5"
                >
                  {t("Radio desde la instalación (metros)")}
                </label>
                <Input
                  id="route-removal-radius"
                  type="number"
                  inputMode="numeric"
                  min={RADIUS_MIN_METERS}
                  max={RADIUS_MAX_METERS}
                  step={1}
                  className="max-w-[12rem]"
                  value={radiusInput}
                  invalid={radiusInvalid}
                  disabled={noGeocoded}
                  aria-invalid={radiusInvalid || undefined}
                  aria-describedby="route-removal-radius-help"
                  onChange={(e) => setRadiusInput(e.target.value)}
                />
                <div id="route-removal-radius-help" aria-live="polite">
                  {radiusInvalid ? (
                    <p className="mt-1.5 text-xs text-status-error">
                      {t("Ingresa un número entero entre 100 y 10.000.")}
                    </p>
                  ) : (
                    <p className="mt-1.5 text-xs text-fg-subtle">
                      {t("Entre 100 y 10.000 metros.")}
                    </p>
                  )}
                </div>
              </div>
            )}
          </fieldset>

          {/* Con "Siempre" la app vieja funciona igual que antes; el error solo
              aparece cuando la política restringe. Con `near_warehouse` sin
              coordenadas ya manda el aviso de la opción: nadie puede quitar nada,
              con ninguna versión. */}
          {mode !== "always" && !(isNearWarehouse && noGeocoded) && (
            <p
              className="flex items-start gap-1.5 text-xs text-status-warning"
              data-testid="route-removal-app-version-note"
            >
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
              {t(
                "Requiere la última actualización de la app de repartidores. Con versiones anteriores, el repartidor verá un error al intentar quitar un pedido.",
              )}
            </p>
          )}

          <div className="border-t border-border pt-4">
            <h4 className="text-sm font-medium text-fg">{t("Tus instalaciones")}</h4>
            <p className="text-xs text-fg-subtle mt-0.5 mb-2">
              {t(
                "Para «Solo cerca de una instalación» cuentan solo las que tienen ubicación en el mapa.",
              )}
            </p>
            {policy.warehouses.length === 0 ? (
              <p className="text-xs text-fg-muted">{t("No tienes instalaciones registradas.")}</p>
            ) : (
              <ul className="flex flex-col gap-1.5" data-testid="route-removal-warehouses">
                {policy.warehouses.map((warehouse, index) => (
                  <li
                    key={warehouse.id ?? `warehouse-${index}`}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span className="min-w-0 truncate text-fg" title={warehouse.label}>
                      {warehouse.label}
                    </span>
                    <Badge tone={warehouse.hasCoordinates ? "success" : "warning"}>
                      {warehouse.hasCoordinates ? t("Con ubicación") : t("Sin ubicación")}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
            <Link
              to={LOCATIONS_TAB_PATH}
              className="mt-2 inline-block text-xs font-medium text-brand-600 hover:underline"
            >
              {t("Ir a Ubicaciones")}
            </Link>
          </div>
        </div>
      </SectionCard>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
          {t("Descartar cambios")}
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!canSave}
          iconLeft={!saving ? <Save size={16} /> : undefined}
        >
          {saving ? t("Guardando…") : t("Guardar cambios")}
        </Button>
      </div>
    </div>
  );
}
